import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import useRealtimeSubscription from '../../../hooks/useRealtimeSubscription';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-400',
    icon: 'Clock',
    pulse: true,
  },
  processing: {
    label: 'Processing',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
    icon: 'RefreshCw',
    pulse: true,
  },
  paid: {
    label: 'Paid',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    icon: 'CheckCircle',
    pulse: false,
  },
  failed: {
    label: 'Failed',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500',
    icon: 'XCircle',
    pulse: false,
  },
  canceled: {
    label: 'Canceled',
    color: 'text-gray-500',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400',
    icon: 'MinusCircle',
    pulse: false,
  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg?.bg} ${cfg?.color} ${cfg?.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot} ${cfg?.pulse ? 'animate-pulse' : ''}`} />
      {cfg?.label}
    </span>
  );
};

const PayoutStatusPanel = ({ userId }) => {
  const [payouts, setPayouts] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [stripeBalance, setStripeBalance] = useState({ pendingAmount: 0, availableAmount: 0 });
  const [loading, setLoading] = useState(true);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState('payouts');

  const loadPayouts = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        ?.from('stripe_payouts')
        ?.select('id, amount, currency, status, description, failure_message, arrival_date, stripe_transfer_id, stripe_payout_id, created_at, transaction_id')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (!error && data) {
        setPayouts(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('[PayoutStatusPanel] loadPayouts error:', err);
    }
  }, [userId]);

  const loadReconciliation = useCallback(async () => {
    if (!userId) return;
    try {
      // Fetch transactions with their payout status for reconciliation
      const { data, error } = await supabase
        ?.from('transactions')
        ?.select('id, amount, status, payout_status, stripe_transfer_id, stripe_payout_id, payout_arrival_date, created_at, campaigns(title)')
        ?.eq('influencer_id', userId)
        ?.in('status', ['approved', 'processing', 'paid'])
        ?.order('created_at', { ascending: false })
        ?.limit(15);

      if (!error && data) {
        setReconciliation(data?.map(t => ({
          id: t?.id,
          campaignName: t?.campaigns?.title || 'Campaign',
          amount: parseFloat(t?.amount) || 0,
          transactionStatus: t?.status,
          payoutStatus: t?.payout_status || 'pending',
          stripeTransferId: t?.stripe_transfer_id || '',
          stripePayoutId: t?.stripe_payout_id || '',
          arrivalDate: t?.payout_arrival_date,
          createdAt: t?.created_at,
          reconciled: !!(t?.stripe_transfer_id),
        })));
      }
    } catch (err) {
      console.error('[PayoutStatusPanel] loadReconciliation error:', err);
    }
  }, [userId]);

  const fetchStripeBalance = useCallback(async () => {
    setStripeLoading(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('stripe-balance', {
        body: {},
      });
      if (!error && data) {
        setStripeBalance({
          pendingAmount: data?.pendingAmount || 0,
          availableAmount: data?.availableAmount || 0,
        });
        // Sync any new payout records from Stripe response
        if (data?.payouts?.length > 0) {
          await syncStripePayouts(data?.payouts);
        }
      }
    } catch (err) {
      console.error('[PayoutStatusPanel] fetchStripeBalance error:', err);
    } finally {
      setStripeLoading(false);
    }
  }, [userId]);

  const syncStripePayouts = async (stripePayout) => {
    if (!userId || !stripePayout?.length) return;
    try {
      for (const payout of stripePayout) {
        await supabase?.from('stripe_payouts')?.upsert({
          user_id: userId,
          stripe_payout_id: payout?.id || '',
          amount: payout?.amount || 0,
          currency: 'usd',
          status: payout?.status || 'pending',
          description: payout?.description || '',
          failure_message: payout?.failureMessage || '',
          arrival_date: payout?.arrivalDate || null,
        }, { onConflict: 'stripe_payout_id', ignoreDuplicates: false });
      }
      await loadPayouts();
    } catch (err) {
      console.error('[PayoutStatusPanel] syncStripePayouts error:', err);
    }
  };

  useEffect(() => {
    if (!userId) return;
    const init = async () => {
      setLoading(true);
      await Promise.all([loadPayouts(), loadReconciliation()]);
      setLoading(false);
    };
    init();
  }, [userId]);

  // Real-time: stripe_payouts table
  useRealtimeSubscription({
    channelName: `stripe-payouts-${userId}`,
    table: 'stripe_payouts',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    enabled: !!userId,
    onChange: () => {
      loadPayouts();
      setLastUpdated(new Date());
    },
  });

  // Real-time: transactions payout_status updates
  useRealtimeSubscription({
    channelName: `tx-payout-${userId}`,
    table: 'transactions',
    filter: userId ? `influencer_id=eq.${userId}` : undefined,
    enabled: !!userId,
    onUpdate: () => loadReconciliation(),
  });

  const pendingCount = payouts?.filter(p => p?.status === 'pending' || p?.status === 'processing')?.length;
  const totalPending = payouts
    ?.filter(p => p?.status === 'pending' || p?.status === 'processing')
    ?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0);
  const reconciledCount = reconciliation?.filter(r => r?.reconciled)?.length;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="CreditCard" size={18} className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Payout Status</h3>
            {lastUpdated && (
              <p className="text-xs text-muted-foreground">
                Updated {lastUpdated?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={fetchStripeBalance}
          disabled={stripeLoading}
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={13} className={stripeLoading ? 'animate-spin' : ''} />
          Sync Stripe
        </button>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-foreground">${totalPending?.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Pending</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-foreground">{pendingCount}</p>
          <p className="text-xs text-muted-foreground mt-0.5">In Progress</p>
        </div>
        <div className="px-4 py-3 text-center">
          <p className="text-lg font-bold text-emerald-600">{reconciledCount}/{reconciliation?.length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Reconciled</p>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {['payouts', 'reconciliation']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-xs font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'payouts' ? 'Payout History' : 'Reconciliation'}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'payouts' ? (
          payouts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <Icon name="Wallet" size={28} className="text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No payouts yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Payouts appear once transactions are approved</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {payouts?.map(payout => (
                <div key={payout?.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      STATUS_CONFIG?.[payout?.status]?.bg || 'bg-gray-50'
                    }`}>
                      <Icon
                        name={STATUS_CONFIG?.[payout?.status]?.icon || 'Clock'}
                        size={15}
                        className={STATUS_CONFIG?.[payout?.status]?.color || 'text-gray-500'}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">
                        {payout?.description || 'Cashback Payout'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {payout?.arrival_date
                          ? new Date(payout.arrival_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : new Date(payout?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {payout?.stripe_payout_id && (
                          <span className="ml-1 opacity-60">· {payout?.stripe_payout_id?.slice(-6)}</span>
                        )}
                      </p>
                      {payout?.failure_message && (
                        <p className="text-xs text-red-500 mt-0.5 truncate">{payout?.failure_message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-sm font-semibold text-foreground">${parseFloat(payout?.amount)?.toFixed(2)}</span>
                    <StatusBadge status={payout?.status} />
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          reconciliation?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <Icon name="GitMerge" size={28} className="text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No transactions to reconcile</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {reconciliation?.map(item => (
                <div key={item?.id} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs font-medium text-foreground truncate">{item?.campaignName}</p>
                    <span className="text-sm font-semibold text-foreground flex-shrink-0">${item?.amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={item?.payoutStatus} />
                      {item?.reconciled ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                          <Icon name="CheckCircle" size={11} />
                          Reconciled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <Icon name="AlertCircle" size={11} />
                          Unmatched
                        </span>
                      )}
                    </div>
                    {item?.stripeTransferId && (
                      <span className="text-xs text-muted-foreground font-mono">
                        {item?.stripeTransferId?.slice(-8)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PayoutStatusPanel;
